import React, { useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Popout from 'components/popout';
import PopoutListActions from 'components/PopoutListActions';
import PromptPopout from 'components/prompt-popout';
import Tooltip from 'components/tooltip';

import IconMoreHoriz from 'icons/icon-more-horiz';
import IconEdit from 'icons/icon-edit';
import IconRemove from 'icons/icon-remove';

const DEFAULT_ACTIONS = ['editName', 'remove'];

const ChecklistItemActions = ({ className = 'actions-holder', innerClassName = 'actions-trigger', allowedActions, itemName, onEditNameClick, onRemove }) => {
    const { t } = useTranslation(['task', 'common']);
    const [visible, setVisible] = useState(false);
    const [innerVisible, setInnerVisible] = useState(false);
    const popoutCtrl = useRef(null);

    const actions = useMemo(() => {
        return (allowedActions || DEFAULT_ACTIONS).filter(action => {
            switch (action) {
                case 'editName':
                    return Boolean(onEditNameClick);

                case 'remove':
                    return Boolean(onRemove);

                default:
                    return true;

            }
        });
    }, [allowedActions, Boolean(onEditNameClick), Boolean(onRemove)]);

    const hidePopout = useCallback(() => {
        popoutCtrl.current?.hide();
    }, [popoutCtrl]);

    const handleTriggerClick = () => {
        popoutCtrl.current?.show();
    };

    const handleShow = useCallback(() => {
        setVisible(true);
    }, []);

    const handleHide = useCallback(() => {
        setVisible(false);
    }, []);

    const handleEditNameClick = useCallback(() => {
        onEditNameClick && onEditNameClick();

        hidePopout();
    }, [onEditNameClick, hidePopout]);

    const handleDeleteClick = useCallback(() => {
        onRemove();

        hidePopout();
    }, [hidePopout, onRemove]);

    const renderAction = useCallback((actionId, onExpand, onCollapse) => {
        switch (actionId) {
            case 'editName':
                return (
                    <div className="item-inner" onClick={handleEditNameClick}>
                        <span className="item-icon"><IconEdit /></span>

                        { t('common:Edit name') }
                    </div>
                );

            case 'remove':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ t(`checklist.actions.removeItem.title`) }
                        text={ t(`checklist.actions.removeItem.description`) }
                        confirmText={ t('common:Delete') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleDeleteClick}>
                            <span className="item-icon"><IconRemove style={{ marginTop: -1 }} /></span>

                            { t('common:Delete') }
                        </div>
                    </PromptPopout>
                );

            default:
                return null;
        }
    }, [t, handleEditNameClick, handleDeleteClick]);

    if (!actions.length) {
        return null;
    }

    return (
        <div className={className + (visible ? ' visible' : '')} onClick={handleTriggerClick}>
            <Tooltip tooltip={t('common:More options')} withTooltipHover={false}>
                <span className={innerClassName}><IconMoreHoriz /></span>
            </Tooltip>

            <Popout
                className="xsmall"
                onShow={handleShow}
                onHide={handleHide}
                provideController={popoutCtrl}
             >
                <PopoutListActions actions={actions} renderAction={renderAction} updatePopoutPosition={popoutCtrl.current?.updatePosition} />
            </Popout>
        </div>
    );
}

export default ChecklistItemActions;
